/**
 * #13 電子送禮 3-step wizard(2026-05-26 channel-less 改版)— LIFF /gift/new
 *
 * 步驟簡化:
 *   1. 選商品(qty 步進器)
 *   2. 寫祝福卡(3 template + 自訂祝福語)
 *   3. 確認送出(僅內部備註 / 可選實體寄送地址)
 *
 * 移除:channel 選擇 + recipient 預先輸入(都改成「拿到 share link 後自由分享」)
 *
 * 送出後 → /gift/sent/:short_code 顯示短連結 + QR + 分享按鈕
 *
 * Phase 1 mock 金流:status="pending_payment_mock";Phase 2 接通 paid webhook 自動 wallet
 * debit + recipient credit。
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts, type Product, type ProductCategory } from "../api/cart";
import { getMe } from "../api/members";
import { createGift, GIFT_TEMPLATES, type GiftTemplate } from "../api/gifts";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import { TAIWAN_CITIES, getDistricts } from "../data/tw-regions";
import { needsProfile, withNext } from "../utils/join";


/**
 * 後端錯誤碼 → 給客戶看的中文。
 * 2026-09-16 事故:客戶送禮失敗,toast 直接吐「送出未成功:sender_not_active」,
 * 客戶看不懂、客服也看不懂。錯誤碼是給我們看的,不是給客戶看的。
 */
const SUBMIT_ERROR_TEXT: Record<string, string> = {
  sender_not_active: "您的會員資格目前無法送禮,請與專屬顧問聯繫",
  cart_empty: "還沒選任何商品",
  invalid_template: "祝福卡樣式有誤,請重新選一張",
  short_code_collision: "系統忙碌中,請再送出一次",
  // 2026-09-20:選到不進存摺的品項(目前是乾燕盞)。送禮是把存摺裡的量轉給對方,
  // 這類品項不在存摺裡,送出去對方領不到,所以後端擋在建單前。
  no_wallet_items: "這項商品目前無法用於送禮,可改送即飲、鮮燉或留白禮盒",
};

type Step = 1 | 2 | 3;

interface SelectedItem {
  product_id: string;
  qty: number;
  name?: string;
  spec?: string;
  price_each?: number;
  bottle_count?: number;
}


export default function GiftWizard() {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const member = useMemberStore((s) => s.member);

  // 商品 catalog
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // step state
  const [step, setStep] = useState<Step>(1);

  // form state
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [template, setTemplate] = useState<GiftTemplate>("elegant");
  const [message, setMessage] = useState("");

  // 寄送地址(可選,實體寄送才需要)
  const [useShipping, setUseShipping] = useState(false);
  const [shipCity, setShipCity] = useState("");
  const [shipDistrict, setShipDistrict] = useState("");
  const [shipDetail, setShipDetail] = useState("");

  // 內部備註(sender 自己看)+ 收禮對象標記(自己看,選填)
  const [note, setNote] = useState("");
  const [recipientMemo, setRecipientMemo] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getProducts();
        setCategories(cats);
        if (!member) {
          const m = await getMe();
          useMemberStore.getState().setMember(m);
        }
      } catch {
        showToast("載入商品失敗", "error");
      } finally {
        setCatalogLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 一鍵加入(2026-09-24)的會員還沒留姓名 / 手機 → 送禮是把存摺的量轉給別人,要對得到人,
  // 先去補、補完回來。後端 create_gift 也擋(profile_required),這裡先攔免得選到第 3 步才被彈走。
  useEffect(() => {
    if (needsProfile(member)) navigate(withNext("/complete-profile", "/gift/new"), { replace: true });
  }, [member, navigate]);

  const flatProducts: Record<string, Product> = useMemo(() => {
    const map: Record<string, Product> = {};
    categories.forEach((cat) => {
      cat.items.forEach((p) => { map[p.id] = p; });
    });
    return map;
  }, [categories]);

  const totalAmount = items.reduce((sum, it) => {
    const p = flatProducts[it.product_id];
    return sum + (p ? p.price * it.qty : 0);
  }, 0);

  function canProceed(s: Step): boolean {
    if (s === 1) return items.length > 0;
    if (s === 2) return true; // 訊息可空,後端帶 default
    return true;
  }

  async function submit() {
    if (!canProceed(1)) {
      showToast("請至少選擇一項商品", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await createGift({
        items: items.map((it) => ({ product_id: it.product_id, qty: it.qty })),
        blessing_template: template,
        blessing_message: message.trim(),
        // recipientMemo 寫到 recipient_name 欄位讓 sender 之後 list 自己看得到
        recipient_name: recipientMemo.trim(),
        shipping_address: useShipping
          ? { city: shipCity, district: shipDistrict, detail: shipDetail }
          : undefined,
        note: note.trim(),
      } as any);
      if (r.status === "ok" && r.short_code) {
        showToast("禮物已備妥", "success");
        navigate(`/gift/sent/${r.short_code}`, { replace: true });
      } else if (r.status === "ok" && r.gift_id) {
        // backward compat:老 backend 沒回 short_code 也能 fallback
        showToast("禮物已備妥", "success");
        navigate(`/gift/sent/${r.gift_id}`, { replace: true });
      } else if (r.status === "insufficient_balance") {
        // 送什麼扣什麼品類 — 告訴 sender 是哪個品類不夠
        const catName = r.category_name || "存摺";
        const unit = r.unit || "瓶";
        showToast(
          `存摺餘額不足:${catName}需 ${r.needed ?? "?"} ${unit},目前 ${r.balance ?? 0} ${unit}`,
          "error",
        );
      } else if (r.status === "no_wallet_items") {
        // 講得出是哪一項就指名道姓,客人才知道要換掉哪個
        const names = (r.names || []).filter(Boolean).join("、");
        showToast(
          names
            ? `${names} 目前無法用於送禮,可改送即飲、鮮燉或留白禮盒`
            : SUBMIT_ERROR_TEXT.no_wallet_items,
          "error",
        );
      } else if (r.status === "profile_required") {
        // 還沒留姓名 / 手機(理論上進頁時就攔了,這是後端的最後防線)→ 去補,補完回來
        showToast("送禮前請先補齊姓名與手機", "info");
        navigate(withNext("/complete-profile", "/gift/new"));
      } else {
        // 認得的錯誤給人話;不認得的才用通用句(錯誤碼留在後端 log,不丟給客戶)
        showToast(
          SUBMIT_ERROR_TEXT[r.status as string] || "送出未成功,請稍後再試或與專屬顧問聯繫",
          "error",
        );
        console.warn("[GiftWizard] createGift 未成功:", r.status);
      }
    } catch {
      showToast("連線異常,請稍後再試", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-32">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => (step === 1 ? navigate("/home") : setStep((step - 1) as Step))}
          className="text-white text-xl"
          aria-label="返回上一步"
        >
          ‹
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold">摯愛分享禮遇</h1>
          <div className="text-xs opacity-80 mt-0.5">步驟 {step} / 3</div>
        </div>
      </header>

      {/* Step indicator */}
      <div className="px-5 pt-4">
        <div className="flex gap-1.5">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`h-1 flex-1 rounded ${n <= step ? "bg-primary" : "bg-line"}`}
            />
          ))}
        </div>
        <div className="text-xs text-muted mt-2">
          {step === 1 && "選擇您要送出的商品"}
          {step === 2 && "挑選祝福卡風格"}
          {step === 3 && "確認並送出"}
        </div>
      </div>

      <div className="px-5 mt-4 space-y-4">
        {step === 1 && (
          <Step1Products
            categories={categories}
            loading={catalogLoading}
            items={items}
            onItemsChange={setItems}
          />
        )}
        {step === 2 && (
          <Step2Card
            template={template}
            onTemplateChange={setTemplate}
            message={message}
            onMessageChange={setMessage}
          />
        )}
        {step === 3 && (
          <Step3Confirm
            items={items}
            flatProducts={flatProducts}
            totalAmount={totalAmount}
            template={template}
            message={message}
            recipientMemo={recipientMemo}
            onRecipientMemoChange={setRecipientMemo}
            useShipping={useShipping}
            onUseShippingChange={setUseShipping}
            shipCity={shipCity}
            shipDistrict={shipDistrict}
            shipDetail={shipDetail}
            onShipCityChange={(v) => { setShipCity(v); setShipDistrict(""); }}
            onShipDistrictChange={setShipDistrict}
            onShipDetailChange={setShipDetail}
            note={note}
            onNoteChange={setNote}
          />
        )}
      </div>

      {/* 底部 step CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-line px-5 py-4 shadow-lg">
        {step < 3 ? (
          <button
            onClick={() => canProceed(step) ? setStep((step + 1) as Step) : showToast("請完成必要欄位", "error")}
            disabled={!canProceed(step)}
            className="btn-primary w-full"
          >
            下一步
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={submitting}
            className="btn-primary w-full"
          >
            {submitting ? "備妥中…" : `備妥禮物連結 · NT$ ${totalAmount.toLocaleString()}`}
          </button>
        )}
      </div>
    </div>
  );
}


// ── Step 1:選商品 ────────────────────────

function Step1Products({
  categories,
  loading,
  items,
  onItemsChange,
}: {
  categories: ProductCategory[];
  loading: boolean;
  items: SelectedItem[];
  onItemsChange: (next: SelectedItem[]) => void;
}) {
  function setQty(pid: string, qty: number) {
    if (qty <= 0) {
      onItemsChange(items.filter((it) => it.product_id !== pid));
      return;
    }
    const found = items.find((it) => it.product_id === pid);
    if (found) {
      onItemsChange(items.map((it) => it.product_id === pid ? { ...it, qty } : it));
    } else {
      onItemsChange([...items, { product_id: pid, qty }]);
    }
  }

  function qtyOf(pid: string): number {
    return items.find((it) => it.product_id === pid)?.qty || 0;
  }

  if (loading) return <div className="text-muted text-sm py-6 text-center">載入商品…</div>;

  return (
    <div className="space-y-4">
      {categories.map((cat) => (
        <section key={cat.key}>
          <div className="text-sm font-bold text-ink">{cat.title}</div>
          <div className="text-xs text-muted">{cat.subtitle}</div>
          <div className="space-y-3 mt-2">
            {cat.items.map((p) => (
              <div key={p.id} className="card p-3 flex items-center gap-3">
                {p.image_url && (
                  <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded object-cover shrink-0" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-ink truncate">{p.name}</div>
                  <div className="text-xs text-muted">{p.spec}</div>
                  <div className="text-sm font-bold text-ink mt-1">NT$ {p.price.toLocaleString()}</div>
                </div>
                <QtyStepper
                  qty={qtyOf(p.id)}
                  onChange={(q) => setQty(p.id, q)}
                />
              </div>
            ))}
          </div>
        </section>
      ))}
      {items.length === 0 && (
        <div className="text-xs text-muted text-center py-4">
          請至少選擇一項商品
        </div>
      )}
    </div>
  );
}

function QtyStepper({ qty, onChange }: { qty: number; onChange: (q: number) => void }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <button
        onClick={() => onChange(qty - 1)}
        disabled={qty === 0}
        className="w-8 h-8 rounded border border-line text-ink disabled:opacity-30"
        aria-label="減少"
      >
        −
      </button>
      <span className="w-7 text-center text-sm font-bold">{qty}</span>
      <button
        onClick={() => onChange(qty + 1)}
        disabled={qty >= 99}
        className="w-8 h-8 rounded border border-line text-ink"
        aria-label="增加"
      >
        ＋
      </button>
    </div>
  );
}


// ── Step 2:祝福卡 ──────────────────────

function Step2Card({
  template,
  onTemplateChange,
  message,
  onMessageChange,
}: {
  template: GiftTemplate;
  onTemplateChange: (t: GiftTemplate) => void;
  message: string;
  onMessageChange: (v: string) => void;
}) {
  const tpl = GIFT_TEMPLATES.find((t) => t.key === template);
  const previewMessage = message.trim() || tpl?.defaultMessage || "";

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted">挑選一張祝福卡。可使用預設文案,或撰寫您的心意。</div>

      <div className="grid grid-cols-3 gap-2">
        {GIFT_TEMPLATES.map((t) => (
          <button
            key={t.key}
            onClick={() => onTemplateChange(t.key)}
            className={`p-3 rounded border text-left ${
              template === t.key
                ? "border-primary bg-primary text-white"
                : "border-line bg-white text-ink"
            }`}
          >
            <div className="text-sm font-bold">{t.label}</div>
            <div className={`text-xxs mt-1 ${template === t.key ? "opacity-80" : "text-muted"}`}>
              {t.subtitle}
            </div>
          </button>
        ))}
      </div>

      <div>
        <label className="label">自訂祝福語(選填,最多 200 字)</label>
        <textarea
          className="input resize-none"
          rows={4}
          value={message}
          onChange={(e) => onMessageChange(e.target.value.slice(0, 200))}
          placeholder={tpl?.defaultMessage || "撰寫您的心意…"}
        />
        <div className="text-xs text-muted text-right mt-1">{message.length} / 200</div>
      </div>

      {/* 預覽 */}
      <div className="card bg-gradient-to-br from-cream to-sage-light/30 p-5 text-center space-y-3">
        <div className="text-xs text-muted">祝福卡預覽</div>
        <div className="text-sm font-bold text-primary">霽雲・恆時珍藏</div>
        <div className="text-ink text-base leading-relaxed whitespace-pre-wrap break-words py-3">
          {previewMessage}
        </div>
        <div className="text-xxs text-muted">— 以霽雲之名</div>
      </div>
    </div>
  );
}


// ── Step 3:確認 ──────────────────────

function Step3Confirm(props: {
  items: SelectedItem[];
  flatProducts: Record<string, Product>;
  totalAmount: number;
  template: GiftTemplate;
  message: string;
  recipientMemo: string;
  onRecipientMemoChange: (v: string) => void;
  useShipping: boolean;
  onUseShippingChange: (b: boolean) => void;
  shipCity: string;
  shipDistrict: string;
  shipDetail: string;
  onShipCityChange: (v: string) => void;
  onShipDistrictChange: (v: string) => void;
  onShipDetailChange: (v: string) => void;
  note: string;
  onNoteChange: (v: string) => void;
}) {
  const tpl = GIFT_TEMPLATES.find((t) => t.key === props.template);
  const previewMessage = props.message.trim() || tpl?.defaultMessage || "";

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="text-sm text-muted mb-3 font-medium">禮物內容</div>
        <div className="space-y-2 text-sm">
          {props.items.map((it) => {
            const p = props.flatProducts[it.product_id];
            if (!p) return null;
            return (
              <div key={it.product_id} className="flex justify-between">
                <span className="flex-1">{p.name} <span className="text-muted">× {it.qty}</span></span>
                <span className="text-ink">NT$ {(p.price * it.qty).toLocaleString()}</span>
              </div>
            );
          })}
        </div>
        <div className="border-t border-line mt-3 pt-3 flex justify-between">
          <span className="text-muted">小計</span>
          <span className="font-bold text-ink">NT$ {props.totalAmount.toLocaleString()}</span>
        </div>
      </div>

      <div className="card bg-gradient-to-br from-cream to-sage-light/30 p-4 text-center space-y-2">
        <div className="text-xxs text-muted">祝福卡 · {tpl?.label}</div>
        <div className="text-ink text-sm leading-relaxed whitespace-pre-wrap break-words py-2">
          {previewMessage}
        </div>
      </div>

      {/* 給誰?(自己看的標記,選填) */}
      <div className="card">
        <div className="text-sm text-muted font-medium mb-2">給誰?(選填,僅您可見)</div>
        <input
          className="input"
          value={props.recipientMemo}
          onChange={(e) => props.onRecipientMemoChange(e.target.value.slice(0, 50))}
          placeholder="例:給張小姐生日 / 給媽媽的母親節"
        />
        <div className="text-xs text-muted mt-1.5 leading-relaxed">
          備妥連結後,您可自由選擇怎麼把這份禮物送到對方手中(LINE 分享、複製連結、QR Code、面對面)。
          此欄位僅供您日後在「我送出的禮物」列表中辨識。
        </div>
      </div>

      {/* 寄送地址(實體寄送時用 — 目前 wallet-to-wallet 流程可不填)*/}
      <div className="card space-y-3">
        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={props.useShipping}
            onChange={(e) => props.onUseShippingChange(e.target.checked)}
          />
          <span>提供寄送地址(若需實體寄送禮盒)</span>
        </label>
        {props.useShipping && (
          <>
            <div>
              <label className="label">縣市</label>
              <select
                className="input"
                value={props.shipCity}
                onChange={(e) => props.onShipCityChange(e.target.value)}
              >
                <option value="">請選擇縣市</option>
                {TAIWAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">區域</label>
              <select
                className="input"
                value={props.shipDistrict}
                onChange={(e) => props.onShipDistrictChange(e.target.value)}
                disabled={!props.shipCity}
              >
                <option value="">{props.shipCity ? "請選擇區域" : "請先選擇縣市"}</option>
                {getDistricts(props.shipCity).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="label">詳細地址</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={props.shipDetail}
                onChange={(e) => props.onShipDetailChange(e.target.value)}
                placeholder="路名、巷弄、號碼、樓層"
                disabled={!props.shipCity}
              />
            </div>
          </>
        )}
      </div>

      <div className="card">
        <div className="text-sm text-muted font-medium mb-2">內部備註(選填,僅您可見)</div>
        <textarea
          className="input resize-none"
          rows={2}
          value={props.note}
          onChange={(e) => props.onNoteChange(e.target.value.slice(0, 500))}
          placeholder="提醒自己這次送禮的場景 / 需求"
        />
      </div>

      <div className="card bg-sage-light/20 p-4 text-xs text-muted leading-relaxed">
        備妥後您會拿到一條私享連結 + QR Code,自由選擇分享方式。
        對方收到連結 → 拆開禮盒 → 領取後,瓶數將自動歸入其燕窩存摺(若為會員)。
      </div>
    </div>
  );
}
